/**
 * Real-time Firestore sync layer.
 *
 * When authenticated, onSnapshot listeners push Firestore changes
 * directly into Zustand stores for cross-device real-time updates.
 */

import { collection, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCookbookStore } from '@/stores/cookbook-store';
import { usePantryStore } from '@/stores/pantry-store';
import { useMealPlanStore } from '@/stores/meal-plan-store';
import { useHistoryStore } from '@/stores/history-store';
import { useDiscoverStore } from '@/stores/discover-store';
import type {
  Recipe,
  PantryItem,
  MealPlanEntry,
  CookingHistoryEntry,
  ShoppingListItem,
} from '@/types';

let unsubscribers: Unsubscribe[] = [];

export function startSync(userId: string): void {
  // Prevent duplicate listeners
  stopSync();

  const basePath = `users/${userId}`;

  // Recipes
  unsubscribers.push(
    onSnapshot(collection(db, `${basePath}/recipes`), (snap) => {
      const recipes = snap.docs.map((d) => d.data() as Recipe);
      useCookbookStore.setState({ recipes });
    })
  );

  // Shopping list
  unsubscribers.push(
    onSnapshot(collection(db, `${basePath}/shoppingList`), (snap) => {
      const shoppingList = snap.docs.map((d) => d.data() as ShoppingListItem);
      useCookbookStore.setState({ shoppingList });
    })
  );

  // Cooking history
  unsubscribers.push(
    onSnapshot(collection(db, `${basePath}/cookingHistory`), (snap) => {
      const cookingHistory = snap.docs
        .map((d) => d.data() as CookingHistoryEntry)
        .sort((a, b) => new Date(b.cookedAt).getTime() - new Date(a.cookedAt).getTime());
      useCookbookStore.setState({ cookingHistory });
      useHistoryStore.setState({ cookingHistory });
    })
  );

  // Pantry items
  unsubscribers.push(
    onSnapshot(collection(db, `${basePath}/pantryItems`), (snap) => {
      const items = snap.docs
        .map((d) => d.data() as PantryItem)
        .sort((a, b) => a.ingredientName.localeCompare(b.ingredientName));
      usePantryStore.setState({ items });
    })
  );

  // Meal plans
  unsubscribers.push(
    onSnapshot(collection(db, `${basePath}/mealPlans`), (snap) => {
      const entries = snap.docs
        .map((d) => d.data() as MealPlanEntry)
        .sort((a, b) => {
          const dateCmp = a.date.localeCompare(b.date);
          if (dateCmp !== 0) return dateCmp;
          return a.mealType.localeCompare(b.mealType);
        });
      useMealPlanStore.setState({ entries });
    })
  );
}

export function stopSync(): void {
  unsubscribers.forEach((unsub) => unsub());
  unsubscribers = [];
  // Reset Discover cache so the next user's fetch isn't tainted by the prior session.
  useDiscoverStore.getState().invalidate();
}
